import React, { FC, MouseEvent } from 'react'

import { css, cx } from '@linaria/core'
import { styled } from '@linaria/react'

import { VoidHandler, WithClassName } from 'common/types'
import { Maybe } from 'generated-graphql/graphql'

import { findTagPart } from './Tag.function'
import { Position, TagTypes } from './Tag.types'
import { UnstyledButton } from '../Button/UnstyledButton'
import { CloseSvg } from '../svgr/CloseSvg'
import { HashTagSvg } from '../svgr/HashTagSvg'
import { Typography } from '../Typography/Typography'

export const Tag: FC<
  {
    tagId: number
    text: string
    type: TagTypes
    onClick?: VoidHandler
    active?: boolean
    match?: Maybe<boolean>
    onTagDelete?: (event: MouseEvent, tagId: number) => void
  } & WithClassName
> = ({
  tagId,
  className,
  text,
  type,
  active = false,
  onClick,
  match,
  onTagDelete,
}) => {
  return (
    <Parent
      className={cx(
        !match && defaultCss,
        match && matchColor[type],
        active && cursorPointerCss,
        className
      )}
      data-name={tagId}
      onClick={onClick}
    >
      <Background>
        <div>{findTagPart(type, Position.Left, match)}</div>
        <div style={{ margin: '0 -1px' }}>
          {findTagPart(type, Position.Center, match)}
        </div>
        <div>{findTagPart(type, Position.Right, match)}</div>
      </Background>

      <Text className={cx(!match ? textColorDefaultCss : textColorActiveCss)}>
        <HashTagSvg color="currentColor" />
        <Truncate fontSize={20} fontWeight={500} tag="span">
          {text}
        </Truncate>
        {onTagDelete && (
          <UnstyledButton
            className={buttonCss}
            onClick={(event) => onTagDelete(event, tagId)}
          >
            <CloseSvg width={16} height={16} />
          </UnstyledButton>
        )}
      </Text>
    </Parent>
  )
}

const buttonCss = css`
  svg {
    path {
      fill: var(--foreground-soft);
    }
  }
`

const Parent = styled.div`
  display: grid;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 36px;
`

const cursorPointerCss = css`
  cursor: pointer;
`

const Text = styled.div`
  flex: 1;
  background-size: cover;
  position: relative;
  display: inline-grid;
  grid-auto-flow: column;
  grid-template-columns: auto;
  gap: var(--spacing-6px);
  grid-gap: var(--spacing-6px);
  justify-content: left;
  align-items: center;
  height: 36px;
  padding: 0 var(--spacing-16px);
`

const textColorDefaultCss = css`
  color: var(--foreground-soft);
`
const textColorActiveCss = css`
  color: var(--foreground-default);
`

const Truncate = styled(Typography)`
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Background = styled.div`
  display: grid;
  grid-template-columns: 16px 1fr 16px;
  gap: -1px;
  grid-gap: -1px;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  transition-property: color, opacity;
  transition-duration: 0.3s;
  transition-timing-function: ease-out;

  html[dir='rtl'] & > div {
    transform: scale(-1, 1);
  }
`

const defaultCss = css`
  ${Background} {
    opacity: 0.64;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        opacity: 1;
      }
    }
  }
`

const firstActiveColorCss = css`
  ${Background} {
    color: var(--pearl-default);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        color: var(--pearl-hover);
      }
    }
  }
`
const secondActiveColorCss = css`
  ${Background} {
    color: var(--rose-default);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        color: var(--rose-hover);
      }
    }
  }
`
const thirdActiveColorCss = css`
  ${Background} {
    color: var(--purpur-default);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        color: var(--purpur-hover);
      }
    }
  }
`
const fourthActiveColorCss = css`
  ${Background} {
    color: var(--sunshine-default);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        color: var(--sunshine-hover);
      }
    }
  }
`

const fifthActiveColorCss = css`
  ${Background} {
    color: var(--splash-default);
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      ${Background} {
        color: var(--splash-hover);
      }
    }
  }
`

export const matchColor = {
  0: firstActiveColorCss,
  1: secondActiveColorCss,
  2: thirdActiveColorCss,
  3: fourthActiveColorCss,
  4: fifthActiveColorCss,
}
